"use client";

import { arktypeResolver } from "@hookform/resolvers/arktype";
import {
  type AddressSearchInput,
  AddressSearchInputSchema,
  type AddressSearchOutput,
} from "@tillix/api-contract/client-schemas";
import { api } from "@tillix/tx-core/orpc/client";
import { Button } from "@tillix/ui-atoms/button";
import { Form } from "@tillix/ui-atoms/form";
import { FormRootError } from "@tillix/ui-atoms/form-root-error";
import { observer } from "mobx-react-lite";
import { FormProvider, useForm } from "react-hook-form";
import AccountField, {
  FIELD_CONFIGS,
} from "@/features/accounts/fields/account-field";
import { useT } from "@/features/i18n/hooks/use-t";

interface AddressSearchFormProps {
  tenantId: string;
  onSearchResults: (results: AddressSearchOutput["results"]) => void;
}

function AddressSearchForm({
  tenantId,
  onSearchResults,
}: AddressSearchFormProps) {
  const { t } = useT();
  const form = useForm<AddressSearchInput>({
    resolver: arktypeResolver(AddressSearchInputSchema),
    defaultValues: {
      tenantId,
      streetName: "",
      zipCode: "",
      houseNumber: "",
      aptNumber: "",
      city: "",
      state: "",
    },
    mode: "onChange",
  });

  const {
    formState: { isSubmitting, isValid, errors },
  } = form;
  const rootError = errors.root?.message;

  const handleSubmit = async (values: AddressSearchInput) => {
    try {
      const response = await api.accountLookup.searchByAddress({
        tenantId,
        streetName: values.streetName.trim(),
        zipCode: values.zipCode.trim(),
        houseNumber: values.houseNumber.trim(),
        aptNumber: values.aptNumber?.trim() || undefined,
        city: values.city?.trim() || undefined,
        state: values.state?.trim() || undefined,
      });
      if (response.results.length === 0) {
        form.setError("root", {
          message: t("quickPay.noAccountsFound"),
        });
        return;
      }
      onSearchResults(response.results);
    } catch (err) {
      form.setError("root", {
        message:
          err instanceof Error ? err.message : t("quickPay.searchFailed"),
      });
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          {t("quickPay.enterServiceAddress")}
        </p>
        {form.formState.isDirty && (
          <button
            className="text-muted-foreground text-xs underline hover:text-foreground"
            onClick={() => form.reset()}
            type="button"
          >
            {t("application.clear")}
          </button>
        )}
      </div>

      <FormProvider {...form}>
        <Form
          className="flex flex-col gap-0"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <div className="grid grid-cols-[minmax(10ch,1fr)_2fr] gap-x-2">
            <AccountField<AddressSearchInput>
              {...FIELD_CONFIGS.houseNumber}
              name="houseNumber"
              required={true}
            />
            <AccountField<AddressSearchInput>
              {...FIELD_CONFIGS.streetName}
              name="streetName"
              required={true}
            />
          </div>
          <AccountField<AddressSearchInput>
            {...FIELD_CONFIGS.aptNumber}
            name="aptNumber"
          />
          <div className="grid grid-cols-2 gap-x-2">
            <AccountField<AddressSearchInput>
              {...FIELD_CONFIGS.state}
              name="state"
            />
            <AccountField<AddressSearchInput>
              {...FIELD_CONFIGS.zipCode}
              name="zipCode"
              required={true}
            />
          </div>
          <FormRootError keyPrefix="address-search" rootError={rootError} />
          <Button
            className="mt-2 w-full"
            disabled={isSubmitting || !isValid}
            type="submit"
          >
            {isSubmitting ? t("quickPay.searching") : t("quickPay.find")}
          </Button>
        </Form>
      </FormProvider>
    </div>
  );
}

export default observer(AddressSearchForm);
